import React from 'react'
import { Link } from '@fs/zion-router'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { bool, number, oneOfType, shape, string } from 'prop-types'
import useDSApplicationMode from '../../../../hooks/useDSApplicationMode'

const cssInfoLine = css`
  padding-bottom: 10px;
  @media screen and (min-width: 320px) and (max-width: 823px) and (orientation: landscape) {
    display: none;
  }
`
const cssLifespan = css`
  font-weight: bold;
`
const getZionStyle = (k) =>
  ({
    colorWhite: '#ffffff',
    colorBlack: '#000000',
    colorBlueGenderLight: '#7DE3F4',
    colorPinkGenderLight: '#FC4E6D',
    colorGrayGenderLight: '#ccccc4',
    colorBlueGender: '#99c4fe',
    colorPinkGender: '#f48eb6',
    colorGrayGender: '#ccccc4',
    fontSizeSmall: '14px',
  }[k] || '')

const getGenderColor = (g) =>
  ({
    male: getZionStyle('colorBlueGenderLight'),
    female: getZionStyle('colorPinkGenderLight'),
    unknown: getZionStyle('colorGrayGenderLight'),
  }[g] || getZionStyle('colorGrayGenderLight'))

export default function PersonInfoLifespan({ gender, living, vitalStrings, personId, showPlaces = true }) {
  const isWeb = useDSApplicationMode() === 'WEB'
  const [t] = useTranslation()
  const cssPlace = () => css`
    border-left: 2px solid ${getGenderColor(gender)};
    margin-left: 12px;
    line-height: 33px;
    padding: 4px 0 4px 12px;
  `

  let ageAtDeath = t(`wcf.person-header.age-unknown`, 'Unknown')

  if (vitalStrings.age) {
    ageAtDeath =
      vitalStrings.estimatedAge === true
        ? t(`wcf.person-header.estimated-age-at-death`, `About ${vitalStrings.age}`)
        : vitalStrings.age.toString()
  }

  return (
    <div css={cssInfoLine}>
      <span css={cssLifespan}>{vitalStrings.lifespan}</span>

      {showPlaces && (
        <>
          <span css={cssPlace(gender)} title={vitalStrings.birthPlaceFull}>
            <strong>{t(`wcf.person-header.birth`, 'Birth')}</strong>:{' '}
            {vitalStrings.birthPlace || t(`wcf.person-header.place-unknown`, 'Unknown')}
          </span>
          <span css={cssPlace(gender)} title={vitalStrings.deathPlaceFull}>
            <strong>{t(`wcf.person-header.death`, 'Death')}</strong>:{' '}
            {vitalStrings.deathPlace || t(`wcf.person-header.place-unknown`, 'Unknown')}
          </span>
        </>
      )}

      {vitalStrings.age !== 'Unknown' && (
        <span css={cssPlace(gender)}>
          <strong>{t(`wcf.person-header.age-at-death${living ? 'living' : ''}`, 'Age')}</strong>: {ageAtDeath}
        </span>
      )}

      {personId && isWeb ? (
        <Link to={`/tree/person/details/${personId}`} external style={{ textDecoration: 'none', color: 'white' }}>
          <span css={cssPlace(gender)} title={vitalStrings.deathPlaceFull}>
            {personId}
          </span>
        </Link>
      ) : (
        personId && (
          <span css={cssPlace(gender)} title={vitalStrings.deathPlaceFull}>
            {personId}
          </span>
        )
      )}
    </div>
  )
}

PersonInfoLifespan.propTypes = {
  gender: string,
  living: bool,
  vitalStrings: shape({
    age: oneOfType([string, number]),
    lifespan: string,
    deathPlace: string,
    deathPlaceFull: string,
    birthPlace: string,
    birthPlaceFull: string,
  }),
  personId: string,
}
